import type { RegisteredComponent } from "@builder.io/sdk-react";
import { Blocks } from "@builder.io/sdk-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";

// ---------------------------------------------------------------------------
// Helper stubs for defaultValue Builder element trees
// ---------------------------------------------------------------------------
const textBlock = (text: string) => ({
  "@type": "@builder.io/sdk:Element" as const,
  component: { name: "Text", options: { text } },
});

const tabButtonBlock = (text: string) => ({
  "@type": "@builder.io/sdk:Element" as const,
  component: { name: "Tab Button", options: { content: [textBlock(text)] } },
});

const tabPanelBlock = (text: string) => ({
  "@type": "@builder.io/sdk:Element" as const,
  component: { name: "Tab Panel", options: { content: [textBlock(text)] } },
});

// ---------------------------------------------------------------------------
// Parent: ContentTabs
//
// Block name "v1: Content Tabs" matches composable exactly so live Builder
// entries continue to render on the Workers app.
//
// Each item stores its trigger and panel content as Builder element trees
// (type "uiBlocks") rather than plain strings. The <Blocks> component from
// the Builder SDK renders those trees in-place with the correct path so the
// Builder editor can visually select and edit individual nested blocks.
// ---------------------------------------------------------------------------

interface ContentTabsItem {
  triggerBlocks?: unknown[];
  panelBlocks?: unknown[];
}

interface ContentTabsProps {
  items?: ContentTabsItem[];
  defaultTab?: number;
  /** Injected by Builder via shouldReceiveBuilderProps — needed as parent ID for <Blocks> */
  builderBlock?: { id?: string };
  attributes?: Record<string, unknown>;
}

const ContentTabs = ({
  items = [],
  defaultTab = 1,
  builderBlock,
  attributes,
}: ContentTabsProps) => {
  // Return null for empty items — a ghost tab strip (empty border) on a live
  // page is worse than nothing. The Builder editor shows the block outline
  // regardless, so editors are not left with an unselectable void.
  if (items.length === 0) return null;

  const safeItems = items;

  // Normalize defaultTab before clamping: NaN (from a cleared number field)
  // propagates through Math.min/max and produces defaultValue="tab-NaN",
  // leaving every panel hidden. Fall back to 1 for any non-finite value.
  const safeDefault = Number.isFinite(defaultTab) ? defaultTab : 1;
  const clampedDefault = Math.max(1, Math.min(safeDefault, safeItems.length));

  const { className, ...restAttributes } = (attributes ?? {}) as {
    className?: string;
    [key: string]: unknown;
  };

  return (
    <div className={cn("w-full", className)} {...restAttributes}>
      <Tabs defaultValue={`tab-${clampedDefault}`}>
        <TabsList className="h-auto flex-wrap justify-start">
          {safeItems.map((item, index) => (
            <TabsTrigger
              key={`tab-${index + 1}`}
              value={`tab-${index + 1}`}
              className="h-auto !whitespace-normal"
            >
              <Blocks
                path={`component.options.items.${index}.triggerBlocks`}
                blocks={(item.triggerBlocks ?? []) as never}
                parent={builderBlock?.id}
              />
            </TabsTrigger>
          ))}
        </TabsList>

        {safeItems.map((item, index) => (
          <TabsContent key={`tab-${index + 1}`} value={`tab-${index + 1}`}>
            <Blocks
              path={`component.options.items.${index}.panelBlocks`}
              blocks={(item.panelBlocks ?? []) as never}
              parent={builderBlock?.id}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export const ContentTabsDefinition = {
  component: ContentTabs,
  name: "v1: Content Tabs",
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  defaultStyles: {
    width: "100%",
  },
  inputs: [
    {
      name: "defaultTab",
      type: "number",
      defaultValue: 1,
      min: 1,
      helperText: "Which tab is active on load (1-indexed)",
    },
    {
      name: "items",
      type: "list",
      defaultValue: [
        {
          triggerBlocks: [tabButtonBlock("Tab")],
          panelBlocks: [tabPanelBlock("Panel content")],
        },
        {
          triggerBlocks: [tabButtonBlock("Tab")],
          panelBlocks: [tabPanelBlock("Panel content")],
        },
      ],
      subFields: [
        {
          name: "triggerBlocks",
          friendlyName: "Tab Button",
          type: "uiBlocks",
          defaultValue: [tabButtonBlock("Tab")],
          helperText:
            "Visual content for the tab button. Use text, image, box, or icon. Avoid nested buttons or links.",
        },
        {
          name: "panelBlocks",
          friendlyName: "Tab Panel",
          type: "uiBlocks",
          defaultValue: [tabPanelBlock("Panel content")],
          helperText: "Any content for the tab panel.",
        },
      ],
    },
  ],
} satisfies RegisteredComponent;

// ---------------------------------------------------------------------------
// Tab Button — standalone block for use inside triggerBlocks.
//
// Name "Tab Button" matches composable so existing Builder entries resolve.
// Renders its `content` (uiBlocks) via <Blocks> — same pattern as composable's
// TabButtonBox. The outer div carries px-4 py-3 spacing matching production.
// ---------------------------------------------------------------------------

interface TabBoxProps {
  content?: unknown[];
  builderBlock?: { id?: string };
  attributes?: Record<string, unknown>;
}

const TabButton = ({ content = [], builderBlock, attributes }: TabBoxProps) => {
  const { className, ...restAttributes } = (attributes ?? {}) as {
    className?: string;
    [key: string]: unknown;
  };
  return (
    <div
      className={cn("flex flex-col px-4 py-3", className)}
      {...restAttributes}
    >
      <Blocks
        path="component.options.content"
        blocks={content as never}
        parent={builderBlock?.id}
      />
    </div>
  );
};

export const TabButtonDefinition = {
  component: TabButton,
  name: "Tab Button",
  noWrap: true,
  canHaveChildren: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  inputs: [
    {
      name: "content",
      type: "uiBlocks",
      defaultValue: [],
    },
  ],
} satisfies RegisteredComponent;

// ---------------------------------------------------------------------------
// Tab Panel — standalone block for use inside panelBlocks.
//
// Name "Tab Panel" matches composable so existing Builder entries resolve.
// Mirror of Tab Button — renders `content` (uiBlocks) via <Blocks>.
// ---------------------------------------------------------------------------

const TabPanel = ({ content = [], builderBlock, attributes }: TabBoxProps) => {
  const { className, ...restAttributes } = (attributes ?? {}) as {
    className?: string;
    [key: string]: unknown;
  };
  return (
    <div
      className={cn("flex flex-col px-4 py-3", className)}
      {...restAttributes}
    >
      <Blocks
        path="component.options.content"
        blocks={content as never}
        parent={builderBlock?.id}
      />
    </div>
  );
};

export const TabPanelDefinition = {
  component: TabPanel,
  name: "Tab Panel",
  noWrap: true,
  canHaveChildren: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  inputs: [
    {
      name: "content",
      type: "uiBlocks",
      defaultValue: [],
    },
  ],
} satisfies RegisteredComponent;
